/**
 * PO Lines Filters Panel — routes to the right filter UI by portal mode.
 *
 * CR-6348: Both Supplier View (SupplierSOLinesFilters) and Customer View
 * (CustomerSectionedFilters) now share the same persistent header pattern.
 * The header with "Filter SO/PO Lines" title and Refresh/Export/Filters buttons
 * stays visible; only the filter sections collapse when showFilters is false.
 *
 * Both views are grouped into labelled sections (Date Range / Company / Supplier /
 * Classification / Order Details) per the supplied diagram, with consistent
 * styling across the platform.
 */
import { POLinesFilters, FilterOptions } from './types';
import { usePortalMode } from '../../../../hooks/usePortalMode';
import { CustomerSectionedFilters } from './filters/CustomerSectionedFilters';
import { SupplierSOLinesFilters } from './filters/SupplierSOLinesFilters';

interface POLinesFiltersPanelProps {
  filters: POLinesFilters;
  filterOptions: FilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  onRefresh: () => void;
  onExport: (format: 'xlsx' | 'csv') => void;
  exporting: boolean;
}

export function POLinesFiltersPanel({
  filters,
  filterOptions,
  onFilterChange,
  onSearch,
  onClearFilters,
  showFilters,
  onToggleFilters,
  onRefresh,
  onExport,
  exporting,
}: POLinesFiltersPanelProps) {
  const { isSupplier } = usePortalMode();

  if (isSupplier) {
    // CR-6348 rework: Supplier view now has persistent header like customer view.
    // The header with Refresh/Export/Filters buttons stays visible; only the
    // filter sections collapse when showFilters is false.
    return (
      <SupplierSOLinesFilters
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        onSearch={onSearch}
        onClearFilters={onClearFilters}
        showFilters={showFilters}
        onToggleFilters={onToggleFilters}
        onRefresh={onRefresh}
        onExport={onExport}
        exporting={exporting}
      />
    );
  }

  return (
    <CustomerSectionedFilters
      filters={filters}
      filterOptions={filterOptions}
      onFilterChange={onFilterChange}
      onSearch={onSearch}
      onClearFilters={onClearFilters}
      showFilters={showFilters}
      onToggleFilters={onToggleFilters}
      onRefresh={onRefresh}
      onExport={onExport}
      exporting={exporting}
    />
  );
}
